import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Inbox, ExternalLink } from 'lucide-react';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@insolech/ui';
import { patientPublicQueries, type AdminReorderRow } from '@insolech/sdk';
import { supabase } from '@/lib/supabase';

/**
 * AdminReordersSection - Phase 4e.
 *
 * Lists patient_reorders rows whose source line belongs to the given order.
 * Each row shows status, message, language preference, and a link to the
 * NEW order created (via the Stripe webhook → complete_patient_reorder RPC).
 */

interface Props {
  orderId: string;
}

const STATUS_VARIANT: Record<AdminReorderRow['status'], 'success' | 'warning' | 'muted' | 'error'> = {
  requested: 'warning',
  reviewed: 'muted',
  fulfilled: 'success',
  rejected: 'error',
};

export function AdminReordersSection({ orderId }: Props) {
  const reorders = useQuery({
    ...patientPublicQueries.orderReordersQuery(supabase, orderId),
    enabled: !!orderId,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Inbox className="h-4 w-4" />
          Patient reorders
        </CardTitle>
      </CardHeader>
      <CardBody>
        {reorders.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : reorders.isError ? (
          <Alert tone="error" title="Could not load reorders">
            {String(reorders.error ?? 'Unknown error')}
          </Alert>
        ) : (reorders.data?.length ?? 0) === 0 ? (
          <p className="text-sm text-text-muted">
            No reorders yet. Patients will appear here after they scan the QR and submit a
            request.
          </p>
        ) : (
          <ul className="divide-y divide-border-subtle">
            {reorders.data?.map((row) => (
              <ReorderRow key={row.id} row={row} />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function ReorderRow({ row }: { row: AdminReorderRow }) {
  return (
    <li className="py-3 space-y-1.5" data-testid={`admin-reorder-row-${row.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant={STATUS_VARIANT[row.status]}>{row.status}</Badge>
        <span className="text-xs font-mono text-text-secondary">{row.short_code}</span>
        <span className="text-xs text-text-muted">·</span>
        <span className="text-xs text-text-secondary">{formatDate(row.created_at)}</span>
        {row.preferred_language ? (
          <>
            <span className="text-xs text-text-muted">·</span>
            <span className="text-xs uppercase text-text-secondary">{row.preferred_language}</span>
          </>
        ) : null}
        {row.preferred_contact ? (
          <>
            <span className="text-xs text-text-muted">·</span>
            <span className="text-xs text-text-secondary">via {row.preferred_contact}</span>
          </>
        ) : null}
      </div>
      {row.patient_message ? (
        <p className="text-sm text-text-primary mt-1 whitespace-pre-wrap rounded-md bg-surface-raised border border-border-subtle px-3 py-2">
          {row.patient_message}
        </p>
      ) : null}
      {row.new_order_id ? (
        <Link
          to={`/admin/orders/${row.new_order_id}`}
          className="inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700"
        >
          <ExternalLink className="h-3 w-3" />
          New order: {row.new_order_number ?? row.new_order_id.slice(0, 8)}
        </Link>
      ) : null}
    </li>
  );
}

function formatDate(s: string): string {
  try {
    return new Date(s).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return s;
  }
}
